import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { captureException } from "@sentry/react";
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import OptionSelect from "./OptionSelect";
import { fetchMergeOptions, triggerMerge } from "./api";
import type { LidarMergeOptions } from "./schemas";
import t from "./translations";

export type MergeTarget = {
  region: string;
  projectId: number;
  projectLabel: string;
};

type Props = {
  target: MergeTarget | null;
  onClose: () => void;
};

export default function MergeDialog({ target, onClose }: Props) {
  const [mergeOptions, setMergeOptions] = useState<LidarMergeOptions | null>(
    null
  );
  const [clearanceSystemId, setClearanceSystemId] = useState<number | null>(
    null
  );
  const [cfgTemplateId, setCfgTemplateId] = useState<number | null>(null);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!target) return;
    setClearanceSystemId(null);
    setMergeOptions(null);
    const loadMergeOptions = async () => {
      try {
        const loadedOptions = await fetchMergeOptions(
          target.region,
          target.projectId
        );
        setMergeOptions(loadedOptions);
        setCfgTemplateId(loadedOptions.default_cfg_template_id);
      } catch (error) {
        captureException(error, {
          tags: { feature: "views.LidarUploadStatus.fetchMergeOptions" },
        });
        toast.error(t.MergeOptionsFailed);
      }
    };
    loadMergeOptions();
  }, [target]);

  const handleMerge = async () => {
    if (!target || clearanceSystemId === null || cfgTemplateId === null) return;
    setMerging(true);
    try {
      await triggerMerge(
        target.region,
        target.projectId,
        clearanceSystemId,
        cfgTemplateId
      );
      toast.success(t.MergeStarted);
      onClose();
    } catch (error) {
      captureException(error, {
        tags: { feature: "views.LidarUploadStatus.triggerMerge" },
      });
      const errorCode = axios.isAxiosError(error)
        ? String(
            error.response?.data?.error_code ??
              error.response?.status ??
              "UNKNOWN"
          )
        : "UNKNOWN";
      toast.error(t.MergeFailed(errorCode));
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog
      open={target !== null}
      onClose={() => (merging ? undefined : onClose())}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {target && t.MergeDialogTitle(target.projectLabel)}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {!mergeOptions && <CircularProgress size={24} />}
          {mergeOptions && (
            <>
              <OptionSelect
                id="merge.clearance-system"
                label={t.ClearanceSystemLabel}
                options={mergeOptions.clearance_systems}
                value={clearanceSystemId}
                disabled={merging}
                requiredText={t.ClearanceSystemRequired}
                onChange={setClearanceSystemId}
              />
              <OptionSelect
                id="merge.cfg-template"
                label={t.CfgTemplateLabel}
                options={mergeOptions.cfg_templates}
                value={cfgTemplateId}
                disabled={merging}
                requiredText={t.CfgTemplateRequired}
                onChange={setCfgTemplateId}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="inherit"
          variant="outlined"
          disabled={merging}
        >
          {t.Cancel}
        </Button>
        <Button
          onClick={handleMerge}
          variant="contained"
          disabled={
            merging || clearanceSystemId === null || cfgTemplateId === null
          }
          data-testid="lidar-upload-status.merge.proceed"
        >
          {t.StartMerge}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
